import styled, { keyframes } from 'styled-components';

import { media } from 'src/styles/breakpoints';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const StyledLogInWrap = styled.div`
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 1.5rem 2rem;
  box-sizing: border-box;
  background: #f1f1f3;
  overflow: auto;

  ${media.md} {
    padding: 4.5rem 1rem 1.5rem;
    align-items: flex-start;
  }
`;

export const BrandBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  min-height: 3.875rem;
  padding: 0 1.5rem;
  gap: 0.625rem;
  z-index: 1;
  background: #fff;
  box-sizing: border-box;

  .logo_image {
    height: 2rem;
    width: auto;
    display: block;
  }

  ${media.md} {
    min-height: 3.5rem;
    padding: 0 1rem;
  }
`;

export const AdminBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: #eef3ff;
  color: #5389ff;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.2;
`;

export const LoginCard = styled.div`
  width: 100%;
  max-width: 29rem;
  padding: 2.25rem 2rem 1.75rem;
  border-radius: 1.75rem;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeIn} 0.45s ease-out;

  ${media.md} {
    padding: 1.75rem 1.25rem 1.5rem;
    border-radius: 1.5rem;
    max-width: 100%;
  }
`;

export const IllustrationWrap = styled.div`
  width: 9.5rem;
  height: 9.5rem;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 1.25rem;
  flex-shrink: 0;
  background: #f1f1f3;

  ${media.md} {
    width: 7.5rem;
    height: 7.5rem;
    margin-bottom: 1rem;
  }
`;

export const Illustration = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const WelcomeTitle = styled.h1`
  margin: 0 0 1.75rem;
  font-size: 1.375rem;
  font-weight: 600;
  line-height: 1.3;
  color: #111;
  text-align: center;

  ${media.md} {
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
  }
`;

export const LoginForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const Field = styled.div`
  position: relative;
  width: 100%;
`;

export const FieldLabel = styled.label`
  position: absolute;
  top: 0;
  left: 0.875rem;
  transform: translateY(-50%);
  padding: 0 0.35rem;
  background: #fff;
  font-size: 0.75rem;
  font-weight: 500;
  color: #656776;
  line-height: 1;
  pointer-events: none;
  z-index: 1;
`;

export const FieldInput = styled.input<{ $invalid?: boolean }>`
  width: 100%;
  height: 3.25rem;
  box-sizing: border-box;
  padding: 0 1rem;
  border: 1px solid ${({ $invalid }) => ($invalid ? '#e00047' : '#dedfe3')};
  border-radius: 0.75rem;
  background: #fff;
  color: #111;
  font-size: 0.9375rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: transparent;
  }

  &:hover {
    border-color: ${({ $invalid }) => ($invalid ? '#e00047' : '#b5b6c0')};
  }

  &:focus {
    border-color: ${({ $invalid }) => ($invalid ? '#e00047' : '#111')};
    box-shadow: ${({ $invalid }) =>
      $invalid ? '0 0 0 3px rgba(224, 0, 71, 0.08)' : '0 0 0 3px rgba(17, 17, 17, 0.06)'};
  }
`;

export const FieldError = styled.span`
  display: block;
  margin-top: 0.45rem;
  padding-left: 0.25rem;
  color: #e00047;
  font-size: 0.75rem;
  line-height: 1.35;
`;

export const PasswordField = styled(Field)`
  ${FieldInput} {
    padding-right: 3rem;
  }
`;

export const PasswordToggle = styled.button`
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: #656776;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: #111;
    background: #f1f1f3;
  }

  &:focus-visible {
    outline: 2px solid #111;
    outline-offset: 1px;
  }
`;

export const SignInButton = styled.button`
  width: 100%;
  height: 3.25rem;
  margin-top: 0.25rem;
  border: none;
  border-radius: 0.75rem;
  background: #111;
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: filter 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    filter: brightness(1.15);
    box-shadow: 0 6px 18px rgba(17, 17, 17, 0.18);
  }

  &:active {
    filter: brightness(0.95);
  }

  &:focus-visible {
    outline: 2px solid #111;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    box-shadow: none;
    filter: none;
  }
`;

export const ForgotLink = styled.a`
  display: block;
  margin-top: 1.25rem;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: #111;
  text-decoration: underline;
  text-underline-offset: 0.15em;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.7;
  }
`;
